import { t, N_ } from '../i18n/i18n';
/**
 * Floating combat text.
 *
 * User report (2026-08-24): "the combat is not clear enough. I do not
 * understand where I am hitting or how much damage I am dealing."
 *
 * Nothing on screen carried a number before this. `combat.result` arrived with
 * an exact damage figure and the only place it went was a line in the
 * monospace playtest overlay in the top-left corner — the opposite side of the
 * screen from where the fight is, in a font the player is not looking at,
 * scrolling away in eight lines.
 *
 * DOM over the canvas rather than sprites in the scene, for the same reason
 * `ActionBar` and `LootPanel` are: text is the one thing the DOM is
 * unambiguously better at, and a `THREE.Sprite` per hit would need a canvas
 * texture per distinct number. The 3D position is projected to screen space
 * each frame, so the number stays pinned to the body it belongs to while it
 * rises.
 *
 * Pooled: elements are reused, never created per hit. A swarm fight produces
 * a hit every few frames and `document.createElement` in that path is exactly
 * the per-frame allocation CLAUDE.md's performance rules call out.
 */

export type CombatTextKind =
  /** Damage the local player dealt. */
  | 'dealt'
  /** Damage the local player took. */
  | 'taken'
  /** Damage between two other parties — a squadmate's hit, or one on them. */
  | 'other'
  | 'heal'
  /** miss / blocked / parried — no number, just the word. */
  | 'nothing'
  /** A killing blow. */
  | 'kill';

interface Floater {
  el: HTMLDivElement;
  /** World position captured at spawn — the number stays where the hit landed rather than chasing a corpse. */
  x: number;
  y: number;
  z: number;
  elapsedMs: number;
  active: boolean;
  /** Horizontal drift, so simultaneous hits on one body don't stack into an illegible pile. */
  driftPx: number;
}

const LIFETIME_MS = 1_100;
const RISE_PX = 46;
/** Above the model origin, roughly chest height — where a hit reads as landing. */
const SPAWN_HEIGHT_M = 1.3;
const MAX_FLOATERS = 24;

const STYLE: Record<CombatTextKind, string> = {
  // Deliberately different weights, not just colours: the single most
  // important distinction is "did that happen to me", and colour alone fails
  // for a colour-blind player and in a scene this dark.
  dealt: 'text-[22px] font-bold text-amber-100',
  taken: 'text-[26px] font-extrabold text-red-400',
  other: 'text-[15px] font-semibold text-text/70',
  heal: 'text-[20px] font-bold text-emerald-300',
  nothing: 'text-[14px] font-medium text-slate-300/70',
  kill: 'text-[26px] font-extrabold text-amber-300',
};

export interface Projector {
  /** World point to CSS pixels within the container, or null when behind the camera. */
  project(x: number, y: number, z: number): { xPx: number; yPx: number } | null;
}

export class DamageNumbers {
  private readonly root: HTMLDivElement;
  private readonly pool: Floater[] = [];

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    // pointer-events-none throughout: this floats over the game and must never
    // eat a click, or a tap, meant for the world.
    this.root.className = 'fixed inset-0 z-[900] pointer-events-none overflow-hidden select-none';
    container.appendChild(this.root);
  }

  /**
   * `text` is pre-formatted by the caller (it knows whether a number belongs).
   * Silently drops the spawn when the pool is exhausted rather than growing
   * without bound — during a swarm fight, the 25th simultaneous number adds
   * nothing a player can read anyway.
   */
  /**
   * Off means the player asked for a quieter fight in settings. Checked here
   * rather than at each caller: the combat, ability and heal handlers all
   * spawn numbers, and three guards is three places to forget one.
   */
  private enabled = true;

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  spawn(text: string, kind: CombatTextKind, x: number, z: number): void {
    if (!this.enabled) return;
    const floater = this.take();
    if (!floater) return;
    floater.el.textContent = text;
    floater.el.className = `absolute -translate-x-1/2 whitespace-nowrap ${STYLE[kind]}`;
    // Every kind gets a hard outline: the dungeon is near-black in places and
    // pure-white text in others, and the number has to survive both.
    floater.el.style.textShadow = '0 0 4px #000, 0 1px 2px #000, 0 0 10px rgba(0,0,0,0.9)';
    floater.x = x;
    floater.y = SPAWN_HEIGHT_M;
    floater.z = z;
    floater.elapsedMs = 0;
    floater.active = true;
    // Deterministic-ish spread from the pool slot rather than Math.random, so
    // two numbers never land exactly on top of each other and the drift is
    // reproducible in a recording.
    floater.driftPx = ((this.pool.indexOf(floater) % 5) - 2) * 13;
    floater.el.style.display = 'block';
  }

  /** Call once per frame, after the camera pose is final. */
  update(dtMs: number, projector: Projector): void {
    for (const floater of this.pool) {
      if (!floater.active) continue;
      floater.elapsedMs += dtMs;
      const t = floater.elapsedMs / LIFETIME_MS;
      if (t >= 1) {
        floater.active = false;
        floater.el.style.display = 'none';
        continue;
      }
      const screen = projector.project(floater.x, floater.y, floater.z);
      if (!screen) { floater.el.style.display = 'none'; continue; }
      floater.el.style.display = 'block';
      floater.el.style.left = `${screen.xPx + floater.driftPx * t}px`;
      // Ease-out rise: fast off the body, slowing as it fades, so the eye is
      // pulled to the hit rather than to the drift.
      floater.el.style.top = `${screen.yPx - RISE_PX * (1 - (1 - t) * (1 - t))}px`;
      // Hold full opacity for the first half — a number that starts fading
      // immediately is one you have to already be looking at to read.
      floater.el.style.opacity = String(t < 0.5 ? 1 : 1 - (t - 0.5) / 0.5);
    }
  }

  private take(): Floater | null {
    for (const floater of this.pool) if (!floater.active) return floater;
    if (this.pool.length >= MAX_FLOATERS) return null;
    const el = document.createElement('div');
    el.style.display = 'none';
    this.root.appendChild(el);
    const floater: Floater = { el, x: 0, y: 0, z: 0, elapsedMs: 0, active: false, driftPx: 0 };
    this.pool.push(floater);
    return floater;
  }

  dispose(): void {
    this.root.remove();
  }
}

/**
 * Which style a `combat.result` should be drawn in, and what it should say.
 *
 * Pure, so the mapping is testable without a DOM: this is the decision that
 * makes a fight readable, and getting "did that happen to me" wrong is worse
 * than not showing it at all.
 */
/** The server's non-hit outcomes (backend CombatOutcome), as the floater says them. */
const OUTCOME_WORD: Record<string, string> = {
  miss: N_('miss'), parried: N_('parried'), dodged: N_('dodged'), blocked: N_('blocked'),
};

export function combatTextFor(
  result: { attackerId: string; defenderId: string; outcome: string; damage?: number; died?: boolean; isRiposte?: boolean },
  localSessionId: string,
  isSquadmate: (sessionId: string) => boolean,
): { text: string; kind: CombatTextKind } | null {
  const iDealt = result.attackerId === localSessionId;
  const iTook = result.defenderId === localSessionId;

  if (result.outcome !== 'hit') {
    // Only ever shown for the local player's own exchanges. A floor full of
    // "miss" from fights you are not in is the log-spam problem this replaces,
    // moved into the middle of the screen.
    if (!iDealt && !iTook) return null;
    const word = OUTCOME_WORD[result.outcome];
    return { text: word ? t(word) : result.outcome, kind: 'nothing' };
  }

  const damage = result.damage ?? 0;
  const suffix = result.isRiposte ? '!' : '';
  if (iTook) return { text: `-${damage}`, kind: 'taken' };
  if (iDealt) return { text: `${damage}${suffix}`, kind: result.died ? 'kill' : 'dealt' };
  // Somebody else's exchange. Shown small and dim when a squadmate is
  // involved — a party member's damage output is information about whether
  // your group is working — and not at all for a fight between strangers.
  if (isSquadmate(result.attackerId) || isSquadmate(result.defenderId)) {
    return { text: `${damage}`, kind: 'other' };
  }
  return null;
}
